import { generatePath } from 'react-router-dom';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';

import { DeviceId } from './types';
import { cloverApi } from './index';

// Clover reader status (docs/CLOVER/reader-status-20261021/PLAN.md, API
// contract). Injected into the Clover API slice from this file so the
// payment endpoints in ./index.ts stay byte-for-byte. `word` and `tone` are
// written by the server; the screens only lay them out.
export type ReaderStatusKey =
  | 'ready'
  | 'login'
  | 'waiting'
  | 'in_sale'
  | 'offline'
  | 'unknown'
  | 'checking'
  // Never checked and its first check is still running (owner 22 Oct:
  // never "Can't check" for "never checked").
  | 'app_closed'
  // Reader hold (24 Oct): another rep's cart or card sale has this reader.
  | 'in_use';

export type ReaderTone = 'ok' | 'bad' | 'warn' | 'muted';

export interface Reader {
  name: string;
  word: string;
  // Reader guard v2 (22 Oct): a stored answer shown instead of a live one.
  // Missing on a server older than v2.
  stale?: boolean;
  tone: ReaderTone;
  deviceId: number;
  eventId: number | null;
  status: ReaderStatusKey;
  checkedAt: string | null;
  eventName: string | null;
  lastSeenAt: string | null;
  statusSince: string | null;
  // Reader hold (24 Oct), worked out for the signed-in rep: the reader is held
  // by this rep's own cart, or by someone else (who, amount, since).
  heldByYou?: boolean;
  inUseBy?: ReaderHolder | null;
}

export interface ReaderHolder {
  name: string | null;
  firstName: string | null;
  amountCents: number | null;
  since: string | null;
  // true: a card sale is running on it; false: only its order screen.
  inSale?: boolean;
}

export interface ReaderClaimRequest {
  eventId: number;
  deviceId: number;
}

export interface ReaderList {
  readers: Reader[];
}

export interface ReaderListRequest {
  eventId?: number;
  // The live dots (24 Oct): the server's own data only, never a Clover call.
  cached?: boolean;
}

export interface ReaderRequest extends DeviceId {
  fresh?: boolean;
}

// A 404 (another company's reader, or a Main without the route yet) must not
// send the app to the Not Found page (api/instance.ts).
const headers = { 'allow-not-found': 'true' };

// Read-only and deliberately tag-free: a sale's Clover mutations invalidate
// the 'Clover' tag, and must never trigger a status re-check mid-sale.
export const readerStatusApi = cloverApi.injectEndpoints({
  endpoints: (build) => ({
    readerStatusList: build.query<ReaderList, ReaderListRequest | void>({
      query: (params) => {
        const { cached, ...rest } = params || {};
        return {
          headers,
          method: 'get',
          url: addParamsToUrl(
            URL.CLOVER_READER_STATUS,
            cached ? { ...rest, cached: 1 } : rest
          ),
        };
      },
    }),

    // Reader hold (24 Oct): Checkout keeps its reader while it is open; a 409
    // means another rep's cart has it. Not tagged: never refetches anything.
    holdReader: build.mutation<{ held: boolean }, ReaderClaimRequest>({
      query: (data) => ({ data, method: 'post', url: URL.CLOVER_READER_CLAIM }),
    }),

    releaseReader: build.mutation<
      { released: number },
      Partial<ReaderClaimRequest>
    >({
      query: (data) => ({
        data,
        method: 'post',
        url: URL.CLOVER_READER_CLAIM_RELEASE,
      }),
    }),

    readerStatus: build.query<Reader, ReaderRequest>({
      query: ({ fresh, deviceId }) => ({
        headers,
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.CLOVER_READER_STATUS_DEVICE, {
            deviceId: String(deviceId),
          }),
          fresh ? { fresh: 1 } : undefined
        ),
      }),
    }),
  }),
});

export const {
  useHoldReaderMutation,
  useReaderStatusQuery,
  useReleaseReaderMutation,
  useLazyReaderStatusQuery,
  useReaderStatusListQuery,
} = readerStatusApi;
